import {
  TEntityResponse,
  TEntityListResponse,
  TCreateCompanyReqBody,
  TCompanyResBody,
  TCompanySummaryResBody,
  TUpdateCompanyReqBody,
  TAreaSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TCompaniesClient = {
  create(body: TCreateCompanyReqBody): Promise<TEntityResponse<'company', TCompanyResBody> | undefined>;
  update(id: string, body: TUpdateCompanyReqBody): Promise<TEntityResponse<'company', TCompanyResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'company', TCompanyResBody> | undefined>;
  list(
    limit: number,
    page: number,
    sort?: string
  ): Promise<TEntityListResponse<'company', TCompanySummaryResBody> | undefined>;
  listCompanyAreas(
    companyId: string,
    limit: number,
    page: number,
    sort?: string
  ): Promise<TEntityListResponse<'area', TAreaSummaryResBody> | undefined>;
};

function CompaniesClient(apiClient: TAPIClient): TCompaniesClient {
  return {
    async create(body: TCreateCompanyReqBody): Promise<TEntityResponse<'company', TCompanyResBody> | undefined> {
      const data = await apiClient.post<TCreateCompanyReqBody, TEntityResponse<'company', TCompanyResBody>>(
        '/companies',
        body
      );

      if (data && data.type !== 'company') {
        throw new Error('Invalid entity type received, expected: company, received: ' + data.type);
      }

      return data;
    },

    async update(
      id: string,
      body: TUpdateCompanyReqBody
    ): Promise<TEntityResponse<'company', TCompanyResBody> | undefined> {
      const data = await apiClient.put<TUpdateCompanyReqBody, TEntityResponse<'company', TCompanyResBody>>(
        `/companies/${id}`,
        body
      );

      if (data && data.type !== 'company') {
        throw new Error('Invalid entity type received, expected: company, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/companies/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'company', TCompanyResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'company', TCompanyResBody>>(`/companies/${id}`);

      if (data && data.type !== 'company') {
        throw new Error('Invalid entity type received, expected: company, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number,
      sort?: string
    ): Promise<TEntityListResponse<'company', TCompanySummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'company', TCompanySummaryResBody>>(
        `/companies?limit=${limit}&page=${page}${sort ? sort : ''}`
      );

      if (data && data.type !== 'company') {
        throw new Error('Invalid entity type received, expected: company, received: ' + data.type);
      }

      return data;
    },

    async listCompanyAreas(
      companyId: string,
      limit: number,
      page: number,
      sort?: string
    ): Promise<TEntityListResponse<'area', TAreaSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'area', TAreaSummaryResBody>>(
        `/companies/${companyId}/areas?limit=${limit}&page=${page}${sort ? sort : ''}`
      );

      if (data && data.type !== 'area') {
        throw new Error('Invalid entity type received, expected: area, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TCompaniesClient, CompaniesClient };
